/**
 * Fan Waitlist admin — waitlist-period-only pacing tool.
 * Once config/platform.waitlistOpen is false, fan signup is instant and this
 * queue is unused. Keep the screen; do not delete it.
 */

import { useEffect, useMemo, useState } from "react";
import AdminLayout from "./AdminLayout";
import {
  approveFansBulk,
  approveFanSingle,
  subscribeFanWaitlist,
} from "../../services/fanWaitlistAdminService";
import "./AdminFanWaitlist.css";

const FILTERS = [
  { key: "waitlisted", label: "Waitlisted" },
  { key: "approved", label: "Approved" },
  { key: "all", label: "All" },
];

function formatJoined(ts) {
  if (!ts) return "—";
  const date = ts.toDate ? ts.toDate() : new Date(ts);
  const diff = Date.now() - date.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${Math.max(1, mins)}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days === 1) return "1 day ago";
  if (days < 7) return `${days} days ago`;
  return date.toLocaleDateString("en-CA", { month: "short", day: "numeric" });
}

function nichesOf(fan) {
  if (Array.isArray(fan.niches)) return fan.niches.filter(Boolean);
  if (Array.isArray(fan.niche)) return fan.niche.filter(Boolean);
  if (fan.niche) return [fan.niche];
  return [];
}

function referredLabel(fan) {
  return fan.referredByHandle || fan.referredByCode || fan.referredBy || null;
}

export default function AdminFanWaitlist() {
  const [fans, setFans] = useState([]);
  const [filter, setFilter] = useState("waitlisted");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    return subscribeFanWaitlist(setFans, (err) => setError(err.message));
  }, []);

  const waitlistedCount = fans.filter((f) => f.approved !== true).length;
  const approvedCount = fans.filter((f) => f.approved === true).length;

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return fans
      .filter((f) => {
        if (filter === "waitlisted") return f.approved !== true;
        if (filter === "approved") return f.approved === true;
        return true;
      })
      .filter((f) => !q || String(f.email || "").toLowerCase().includes(q))
      .sort((a, b) => {
        const at = a.joinedAt?.toMillis?.() || a.joinedAt?.seconds * 1000 || 0;
        const bt = b.joinedAt?.toMillis?.() || b.joinedAt?.seconds * 1000 || 0;
        return bt - at;
      });
  }, [fans, filter, search]);

  const toggle = (email) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(email)) next.delete(email);
      else next.add(email);
      return next;
    });
  };

  const toggleAllVisible = () => {
    const waitlistedVisible = visible.filter((f) => f.approved !== true).map((f) => f.email);
    setSelected((prev) => {
      const allSelected = waitlistedVisible.every((e) => prev.has(e));
      if (allSelected) return new Set();
      return new Set(waitlistedVisible);
    });
  };

  const handleApprove = async (email) => {
    try {
      setBusy(true);
      setError("");
      await approveFanSingle(email);
      setFans((prev) =>
        prev.map((f) =>
          f.email === email ? { ...f, approved: true, approvedAt: new Date() } : f
        )
      );
      setSelected((prev) => {
        const next = new Set(prev);
        next.delete(email);
        return next;
      });
    } catch (err) {
      setError(err.message || "Approve failed.");
    } finally {
      setBusy(false);
    }
  };

  const handleBulkApprove = async () => {
    const emails = [...selected].filter((email) => {
      const fan = fans.find((f) => f.email === email);
      return fan && fan.approved !== true;
    });
    if (!emails.length) return;
    try {
      setBusy(true);
      setError("");
      await approveFansBulk(emails);
      const approvedSet = new Set(emails);
      setFans((prev) =>
        prev.map((f) =>
          approvedSet.has(f.email) ? { ...f, approved: true, approvedAt: new Date() } : f
        )
      );
      setSelected(new Set());
    } catch (err) {
      setError(err.message || "Bulk approve failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AdminLayout>
      <div className="admin-fan-shell">
        <div className="admin-fan-header-row">
          <div className="admin-page-header">
            <div className="admin-eyebrow">Fan Pipeline</div>
            <div className="admin-page-title">Fan <em>Waitlist</em></div>
            <span className="admin-page-hand">— bring fans on in batches, same as creators</span>
          </div>
          <div className="admin-fan-stat-strip">
            <div className="admin-fan-stat">
              <div className="admin-fan-stat-num gold">{waitlistedCount}</div>
              <div className="admin-fan-stat-label">Waitlisted</div>
            </div>
            <div className="admin-fan-stat">
              <div className="admin-fan-stat-num">{approvedCount}</div>
              <div className="admin-fan-stat-label">Approved</div>
            </div>
          </div>
        </div>

        <div className="admin-fan-toolbar">
          <div className="admin-filter-pills">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                type="button"
                className={`admin-pill${filter === f.key ? " active" : ""}`}
                onClick={() => setFilter(f.key)}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className="admin-fan-search">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
              <circle cx="11" cy="11" r="7" />
              <path d="m21 21-4.3-4.3" />
            </svg>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by email"
            />
          </div>
        </div>

        {selected.size > 0 && (
          <div className="admin-fan-bulk">
            <div>
              <span className="admin-bulk-count" style={{ fontWeight: 700, marginRight: 10 }}>
                {selected.size} selected
              </span>
              <span style={{ fontSize: 12, color: "var(--text-muted)" }}>ready to approve</span>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button type="button" className="admin-btn admin-btn-niche" onClick={handleBulkApprove} disabled={busy}>
                Approve Selected ({selected.size})
              </button>
              <button type="button" className="admin-btn admin-btn-ghost" onClick={() => setSelected(new Set())}>
                Clear Selection
              </button>
            </div>
          </div>
        )}

        {error && <p style={{ color: "var(--red)", fontSize: 13, marginBottom: 12 }}>{error}</p>}

        <div className="admin-fan-table-wrap">
          <table className="admin-fan-table">
            <thead>
              <tr>
                <th style={{ width: 20 }}>
                  <input
                    type="checkbox"
                    className="admin-row-checkbox"
                    onChange={toggleAllVisible}
                    checked={
                      visible.filter((f) => f.approved !== true).length > 0 &&
                      visible.filter((f) => f.approved !== true).every((f) => selected.has(f.email))
                    }
                  />
                </th>
                <th>Fan</th>
                <th>Niches Selected</th>
                <th>Referred By</th>
                <th>Joined</th>
                <th style={{ textAlign: "right" }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {visible.length === 0 ? (
                <tr>
                  <td colSpan={6} style={{ color: "var(--text-muted)", padding: 28 }}>
                    No fans in this view.
                  </td>
                </tr>
              ) : (
                visible.map((fan) => {
                  const niches = nichesOf(fan);
                  const ref = referredLabel(fan);
                  const initial = String(fan.email || "?").charAt(0).toUpperCase();
                  return (
                    <tr key={fan.email}>
                      <td>
                        {fan.approved !== true && (
                          <input
                            type="checkbox"
                            className="admin-row-checkbox"
                            checked={selected.has(fan.email)}
                            onChange={() => toggle(fan.email)}
                          />
                        )}
                      </td>
                      <td>
                        <div className="admin-fan-cell">
                          <div className="admin-fan-avatar">{initial}</div>
                          <div className="admin-fan-email">{fan.email}</div>
                        </div>
                      </td>
                      <td>
                        {niches.length
                          ? niches.map((n) => (
                              <span key={n} className="admin-fan-niche">{n}</span>
                            ))
                          : <span className="admin-fan-referral none">—</span>}
                      </td>
                      <td className={`admin-fan-referral${ref ? "" : " none"}`}>
                        {ref || "— none"}
                      </td>
                      <td className="admin-fan-date">{formatJoined(fan.joinedAt)}</td>
                      <td>
                        <div className="admin-action-cell">
                          {fan.approved !== true ? (
                            <button
                              type="button"
                              className="admin-icon-btn approve"
                              title="Approve"
                              disabled={busy}
                              onClick={() => handleApprove(fan.email)}
                            >
                              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M20 6 9 17l-5-5" />
                              </svg>
                            </button>
                          ) : (
                            <span className="admin-fan-date">Approved</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <p className="admin-fan-note">
          Waitlist-period tool only. When <code>config/platform.waitlistOpen</code> is false,
          fans onboard instantly and this queue is unused — keep the page for history, do not delete.
        </p>
      </div>
    </AdminLayout>
  );
}
